import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { catchError, Observable, tap } from 'rxjs';
import {
  buildUpdateMedicalHistoryRequest,
  HealthConditions,
  mapHealthConditions,
  mapPatientMedicalHistory,
  PatientMedicalHistory,
} from '../../models';
import { ApiService, SentryService } from '../../services';
import {
  FetchPatientMedicalHistory,
  FetchHealthConditions,
  UpdatePatientMedicalHistory,
} from './medical-history.actions';
import { ApiStoreData, defaultApiStoreData, MissingApiRequestDataError } from '../utils';
import { PatientState } from '../patient/patient.state';

export interface PatientMedicalHistoryStrings {
  medicationsStrArray: string[];
  medicalConditionsStr: string;
  allergyConditionsStr: string;
}

export interface MedicalHistoryStateModel {
  medicalHistory: ApiStoreData<PatientMedicalHistory | null>;
  healthConditions: ApiStoreData<HealthConditions | null>;
  updateMedicalHistory: ApiStoreData<null>;
}

@State<MedicalHistoryStateModel>({
  name: 'patientMedicalHistory',
  defaults: {
    medicalHistory: defaultApiStoreData,
    healthConditions: defaultApiStoreData,
    updateMedicalHistory: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class MedicalHistoryState {
  constructor(private apiService: ApiService, private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static patientMedicalHistoryState(state: MedicalHistoryStateModel): ApiStoreData<PatientMedicalHistory | null> {
    return state.medicalHistory;
  }

  @Selector()
  static patientMedicalHistoryStrings(state: MedicalHistoryStateModel): PatientMedicalHistoryStrings {
    let medications = (state.medicalHistory.data?.medications || []).map((i) => i.name);
    const strs = {
      medicationsStrArray: medications.length ? medications : ['None'],
      medicalConditionsStr:
        (state.medicalHistory.data?.medicalConditions || [])
          // TODO: figure out why .map does not cause undefined error
          .map((i) => i)
          .sort()
          .join(', ') || 'None',
      allergyConditionsStr:
        (state.medicalHistory.data?.allergyConditions || [])
          // TODO: figure out why .map does not cause undefined error
          .map((i) => i)
          .sort()
          .join(', ') || 'None',
    };

    return strs;
  }

  @Selector()
  static healthConditionsState(state: MedicalHistoryStateModel): ApiStoreData<HealthConditions | null> {
    return state.healthConditions;
  }

  @Selector()
  static updateMedicalHistoryState(state: MedicalHistoryStateModel): ApiStoreData<null> {
    return state.updateMedicalHistory;
  }

  @Action(FetchPatientMedicalHistory)
  fecthMedicalHistory(ctx: StateContext<MedicalHistoryStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.medicalHistory.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'medicalHistory' });
    }

    return this.apiService.getMedicalHistory(patientId).pipe(
      tap({
        next: (response) => {
          const data = mapPatientMedicalHistory(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'medicalHistory' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'medicalHistory' }))
    );
  }

  @Action(FetchHealthConditions)
  fecthHealthConditions(ctx: StateContext<MedicalHistoryStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.healthConditions.isLoading = true;
      })
    );

    return this.apiService.getHealthConditions().pipe(
      tap({
        next: (response) => {
          const data = mapHealthConditions(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'healthConditions' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'healthConditions' }))
    );
  }

  @Action(UpdatePatientMedicalHistory)
  updateMedicalHistory(ctx: StateContext<MedicalHistoryStateModel>, action: UpdatePatientMedicalHistory) {
    ctx.setState(
      produce((draft) => {
        draft.updateMedicalHistory.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const healthConditions = ctx.getState().healthConditions.data;

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'updateMedicalHistory' });
    }

    if (!healthConditions) {
      return this.handleError({
        ctx,
        error: new MissingApiRequestDataError(['Missing health conditions']),
        stateKey: 'updateMedicalHistory',
      });
    }

    return this.apiService.updateMedicalHistory(patientId, buildUpdateMedicalHistoryRequest(action.formValues)).pipe(
      tap({
        next: (response) => {
          const data = mapPatientMedicalHistory(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'medicalHistory' });
          this.handleSuccess({ ctx, stateKey: 'updateMedicalHistory' });
        },
      }),
      catchError(
        (error: Error): Observable<any> =>
          this.handleError({ ctx, error, stateKey: 'updateMedicalHistory', resetData: false })
      )
    );
  }
}
